/**
 * @file planningRows.js
 * @version 1.6.1
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-17
 * @modified 2026-10-23
 * @description Row-key helpers shared by the two transport planning boards
 *              (TransportPlanning = bulk, HaulageJobsPlanning = haulage/bag).
 *
 *              Both boards key their PO-scoped handlers (sequence, scheduled
 *              time, door config) by purchase_order_id. Two kinds of row have
 *              no PO: haulage jobs, and stock-supplied order lines (supplier
 *              H J Ward Agriculture — picked from our own stock, so no PO is
 *              ever raised). Those rows get a prefixed synthetic key on fetch so
 *              they stay unique instead of all colliding on null, and the
 *              handlers dispatch on the prefix to the right endpoint/key.
 *
 *              1.4.0 (2026-10-07): boardSequence (the board order as the
 *              server stores it) and saveDayPlan (one lorry's Loads and Drops
 *              overrides, PUT /api/transport/planning/day-plan).
 *
 *              1.5.0 (2026-10-12): Loads and Drops scrapped for lorry day
 *              planning — saveDayPlan gone; saveDropTimes (PUT
 *              /planning/line-plans) and saveLorryDay (PUT /planning/lorry-day).
 *
 *              1.6.1 (2026-10-23): saveLorryDay takes { keepalive } (the Lorry
 *              day window saving as the page goes away) and sends no door
 *              layouts when it is given none.
 *
 *              1.6.0 (2026-10-15, Cam): a refused order's redelivery row
 *              (utils/redelivery, key `rd:<redelivery id>:<order line id>`)
 *              keeps its time, board order, door layout and run by its ORDER
 *              LINE on the redelivery date, as a stock row does — so it is
 *              keyed `SL-<order line id>` (boardKeyOf), boardSequence sends that
 *              line, its reference is its sales order and its change history
 *              is the order line's.
 */

import React from 'react';
import { API_URL } from '../config';
import EntityLink from '../components/EntityLink';
import { isOptionCandidate } from './planningDay';
import { storageLineIdOf } from './redelivery';

export const HAULAGE_KEY_PREFIX = 'HO-';
export const STOCK_KEY_PREFIX = 'SL-';

export const isHaulageKey = (id) => String(id).startsWith(HAULAGE_KEY_PREFIX);
export const isStockKey = (id) => String(id).startsWith(STOCK_KEY_PREFIX);

export const haulageJobIdOf = (id) => Number(String(id).slice(HAULAGE_KEY_PREFIX.length));
export const stockLineIdOf = (id) => Number(String(id).slice(STOCK_KEY_PREFIX.length));

/**
 * The key a board row is grouped and stored by (its `purchase_order_id` on the
 * board): the PO for a bought-in row; `SL-<order line>` for a stock row and for
 * a redelivery row (stored by its order line on the redelivery date). Haulage
 * jobs are keyed by each board itself.
 */
export function boardKeyOf(o) {
  if (o && o.is_redelivery) return `${STOCK_KEY_PREFIX}${storageLineIdOf(o)}`;
  if (o && o.is_stock_line) return `${STOCK_KEY_PREFIX}${o.order_line_id}`;
  return o ? o.purchase_order_id : null;
}

// The vehicle an external haulier's driver is put on until the registration is
// known (its payload is set per driver per day).
/*
 * Runs — "Combine into one run". Rows sharing a run_key are one load for one
 * vehicle whatever their collection times (vehicleLoad.segmentLoads). The key is
 * stored where the time is: per PO, per stock line, per haulage job — so every
 * row on a PO joins the run with it.
 */
export const newRunKey = () => `run-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

/** The rows' storage ids, split by kind, for PUT /api/transport/planning/run. */
export function runRowIds(rows) {
  const ids = { purchaseOrderIds: new Set(), orderLineIds: new Set(), haulageJobIds: new Set() };
  for (const r of rows || []) {
    const k = r && r.purchase_order_id;
    if (k == null || k === '') continue;
    if (isHaulageKey(k)) ids.haulageJobIds.add(haulageJobIdOf(k));
    else if (isStockKey(k)) ids.orderLineIds.add(stockLineIdOf(k));
    else ids.purchaseOrderIds.add(Number(k));
  }
  return {
    purchaseOrderIds: [...ids.purchaseOrderIds],
    orderLineIds: [...ids.orderLineIds],
    haulageJobIds: [...ids.haulageJobIds]
  };
}

/** Put rows into one run (runKey) or take them out of theirs (null). Throws on failure. */
export async function saveRun(date, rows, runKey) {
  const res = await fetch(`${API_URL}/api/transport/planning/run`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ date, runKey, ...runRowIds(rows) })
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || 'Could not save the run');
}

/** Rows' run keys stamped locally, on every row sharing a PO/key with them (as the server stores it). */
export function withRunKey(allRows, rows, runKey) {
  const keys = new Set((rows || []).map(r => String(r.purchase_order_id)));
  return allRows.map(o => (keys.has(String(o.purchase_order_id)) ? { ...o, run_key: runKey } : o));
}

/**
 * The board order as PUT /planning/update-sequence stores it: planned rows
 * only (an option-date candidate is planned on another day), keyed by PO, or
 * by order line for a stock row.
 */
export const boardSequence = (rows) => (rows || [])
  .filter(order => !isOptionCandidate(order))
  .map((order, index) => (order.is_stock_line || order.is_redelivery
    ? { order_line_id: storageLineIdOf(order), sequence: index + 1 }
    : { purchase_order_id: order.purchase_order_id, sequence: index + 1 }));

async function putJson(url, body, fallback, { keepalive = false } = {}) {
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
    ...(keepalive ? { keepalive: true } : {})
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || !data.success) throw new Error(data.error || fallback);
  return data;
}

/**
 * Drop times typed on the board: [{ orderLineId, dropTime }] (null clears).
 * Throws with the server's reason (switched off, not set up, past or locked day).
 */
export function saveDropTimes(date, lines) {
  return putJson(`${API_URL}/api/transport/planning/line-plans`, { date, lines }, 'Could not save the drop time');
}

/**
 * One lorry's day from the Lorry day window: { lorryDay?, lines, collectTimes }
 * (lorryDay left out = the door layouts unchanged). `keepalive` for a save
 * sent as the page goes away. Throws with the server's reason.
 */
export function saveLorryDay(date, lorryKey, { lorryDay, lines, collectTimes }, { keepalive = false } = {}) {
  return putJson(`${API_URL}/api/transport/planning/lorry-day`, { date, lorryKey, lorryDay, lines, collectTimes }, 'Could not save the lorry’s day', { keepalive });
}

export const UNKNOWN_VEHICLE = 'Unknown';
// Our own haulage, whatever case it was typed in.
export const isExcelHaulier = (h) => (h || '').trim().toLowerCase() === 'excel agri';

/**
 * Reference shown in the board's PO column (and on paperwork): the PO number for
 * bought-in rows, the sales order number for stock rows, which have no PO.
 */
export const rowReference = (o) => (o && (o.purchase_order_number || o.order_number)) || '';

/** Change-log endpoint for a board row — by PO, or by order line for stock rows. */
export const transportChangesUrl = (order) => (order.is_stock_line || order.is_redelivery
  ? `${API_URL}/api/audit/transport-changes/line/${storageLineIdOf(order)}`
  : `${API_URL}/api/audit/transport-changes/${order.purchase_order_id}`);

/**
 * The board's reference cell, as a link to whatever the reference actually IS.
 *
 * One string, three entity types: a bought-in row shows a PO number, a stock row
 * shows its sales order number, and a haulage job shows its job number. Getting
 * this wrong is silent — PurchaseOrderModal resolves on xero_purchase_order_id,
 * so handing it the integer po.id renders "not found" rather than an error (see
 * CLAUDE.md). Both boards render the same cell, so the branch lives here once.
 *
 * Haulage jobs have no registered entity type, so they stay plain text.
 */
export const RowReferenceLink = ({ order }) => {
  const text = rowReference(order);
  if (!text) return null;
  if (order.is_haulage_job) return <>{text}</>;
  // A stock row, and a redelivery (nothing bought for it), name their order.
  if (order.is_stock_line || order.is_redelivery) {
    return <EntityLink type="customer_order" id={order.id} draggable={false}>{text}</EntityLink>;
  }
  return (
    <EntityLink type="purchase_order" id={order.xero_purchase_order_id} draggable={false}>
      {text}
    </EntityLink>
  );
};
